/**
 * Recognising a Bower entity behind an in-app link.
 *
 * Bird writes entity references as ordinary markdown links to app paths
 * (`/notes/abc`). To render one as an entity — icon, accent, hover preview —
 * the renderer has to work out WHICH entity from the href alone, because
 * that's all the markdown carries.
 *
 * Kept pure and separate from the component so the URL edge cases (query
 * strings, trailing slashes, protocol-relative URLs, deeper paths) are
 * testable without a DOM.
 */

import type { EntityType } from '../../../theme/entityIcons';

export interface ParsedEntityHref {
  readonly entityType: EntityType;
  readonly id: string;
}

/**
 * First path segment → entity type.
 *
 * `/notes/:id` is still shared by note, summary AND protocol_annotations — see
 * `detailRoutePath` in shared's ARTIFACT_TYPE_CONFIG. The href genuinely cannot
 * tell those apart, so it reports 'note' and the hover preview (which fetches
 * the artifact) states the real type. Don't "fix" that by guessing from the
 * link text.
 *
 * `protocol` LEFT that group on 2026-09-16 and has its own `/protocols/:id`.
 * Adding the segment here is not cosmetic: without it a protocol citation falls
 * through to null, which costs it the entity icon and the hover preview, and
 * under `internalLinkPolicy="label"` renders as plain text that cannot be
 * clicked at all. A route the app has and this map does not is a link that
 * quietly stops working.
 */
/**
 * `Object.create(null)`, NOT an object literal. A literal inherits from
 * Object.prototype, so `SEGMENT_TO_TYPE['constructor']` (or `__proto__`,
 * `toString`, `valueOf`…) resolves TRUTHY and this function happily returns
 * `{ entityType: <Function> }`. EntityLink then does
 * `ENTITY_ICONS[<Function>].icon` and throws — and the `a` renderer is not
 * inside any ErrorBoundary, so a single link like `[x](/constructor/y)` in
 * Bird's output takes down the entire message.
 *
 * Bird restates content from connectors, imports and web search, so these
 * hrefs are not fully trusted input.
 */
const SEGMENT_TO_TYPE: Record<string, EntityType> = Object.assign(Object.create(null), {
  notes: 'note',
  protocols: 'protocol',
  attachments: 'attachment',
  equipment: 'equipment',
  spreadsheets: 'spreadsheet',
  // A link artifact's /links/:id redirect route (docs/planning/link-artifacts.md).
  links: 'link',
  // Collections carry four historical spellings; all resolve to the same
  // entity, and legacy paths still appear in older sessions' stored messages.
  collections: 'project',
  collection: 'project',
  projects: 'project',
  project: 'project',
});

/**
 * The entity a same-app href points at, or null when it isn't an entity link
 * (a list page, an unknown route, or anything leaving the app).
 */
export function parseEntityHref(href: string): ParsedEntityHref | null {
  // Same guard as isInternalUrl: `//evil.example/notes/x` is protocol-relative
  // and leaves the app, so it must never be dressed up as a Bower entity.
  if (!href.startsWith('/') || href.startsWith('//')) return null;

  const path = href.split(/[?#]/, 1)[0];
  const segments = path.split('/').filter(Boolean);
  // Exactly `/<segment>/<id>` — `/notes` is the list page, and anything deeper
  // is a sub-route this link treatment doesn't claim to understand.
  if (segments.length !== 2) return null;

  const entityType = SEGMENT_TO_TYPE[segments[0]];
  if (!entityType) return null;

  // `decodeURIComponent` THROWS on a malformed sequence — `/notes/50%` is
  // enough. Same blast radius as the prototype keys above: unguarded, it kills
  // the whole message render.
  let id: string;
  try {
    id = decodeURIComponent(segments[1]);
  } catch {
    return null;
  }
  // The id is interpolated into a same-origin authenticated fetch for the hover
  // preview, so it must not smuggle a query, fragment or extra path segment.
  if (!id || /[?#/\\]/.test(id)) return null;

  return { entityType, id };
}
